import React, { useEffect, useState } from "react";
import { Panel } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Trash2, Download, CheckCircle2, Mail, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/pages/admin/api";

export default function ContactMessages() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status !== "all") params.set("status", status);
    const r = await api.get(`/contact?${params.toString()}`);
    setItems(r.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const patch = async (id, patch) => {
    const r = await api.patch(`/contact/${id}`, patch);
    setItems((prev) => prev.map((x) => (x.id === id ? r.data : x)));
  };
  const del = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/contact/${id}`);
    setItems(items.filter((x) => x.id !== id));
    toast.success("Deleted");
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("si-admin-token");
    const r = await fetch(`${API}/contact/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) return toast.error("Export failed");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "contacts.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Panel
      title="Contact Messages"
      subtitle={`${items.length} messages`}
      actions={
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
              <SelectItem value="replied">Replied</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="export-csv" variant="outline" onClick={exportCsv} className="rounded-xl"><Download size={14} className="mr-1" /> Export CSV</Button>
        </div>
      }
    >
      <div className="mb-4 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input data-testid="msg-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name, email or message…" className="pl-9" />
      </div>

      {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">No messages match.</div>}
      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.id} className={`rounded-xl border p-4 ${c.reply_status === "unread" ? "border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-950/10" : "border-border"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(expanded === c.id ? null : c.id)}>
                <div className="text-sm font-semibold truncate flex items-center gap-2">
                  {c.name}
                  {c.is_spam && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">Spam</span>}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.reply_status === "unread" ? "bg-blue-100 text-blue-700" : c.reply_status === "replied" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{c.reply_status}</span>
                </div>
                <div className="text-xs text-muted-foreground">{c.email} · {new Date(c.created_at).toLocaleString()}</div>
                <p className={`mt-2 text-sm whitespace-pre-line ${expanded === c.id ? "" : "line-clamp-2"}`}>{c.message}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button title="Mark read" onClick={() => patch(c.id, { reply_status: "read" })} className="w-8 h-8 rounded-lg hover:bg-accent grid place-items-center text-slate-600"><Mail size={13} /></button>
                <button title="Mark replied" onClick={() => patch(c.id, { reply_status: "replied" })} className="w-8 h-8 rounded-lg hover:bg-accent grid place-items-center text-emerald-600"><CheckCircle2 size={13} /></button>
                <button title="Toggle spam" onClick={() => patch(c.id, { is_spam: !c.is_spam })} className="w-8 h-8 rounded-lg hover:bg-accent grid place-items-center text-orange-600"><ShieldOff size={13} /></button>
                <button title="Delete" onClick={() => del(c.id)} className="w-8 h-8 rounded-lg hover:bg-accent grid place-items-center text-red-500"><Trash2 size={13} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}
